/**
 * One share-dialog read through TanStack Query (share properties, snapshots, item properties, the protocol). The key
 * follows the pinned five-element shape (`['azure-storage', connectionId, share, resourceKey, purpose]`), so a
 * connection reset or a share-scoped invalidation reaches it. The read belongs to the open dialog: when the last
 * observer leaves, an in-flight request is cancelled in main and the cached answer dropped (the next open reads fresh).
 */
import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { containerScopeKey, storageResourceKey, type FileServiceRef, type StorageOperationError } from '../../../../../types/azureStorage';
import { toStorageOperationError } from '../../services/azureStorageApi';
import { STORAGE_QUERY_ROOT } from '../../services/storageListingKeys';
import type { ShareRequestHandle } from '../shareApi';

type Args<T> = { connectionId: string; resource: FileServiceRef; purpose: string; enabled: boolean; start: () => ShareRequestHandle<T> };

export type ShareRead<T> = { data?: T; error?: StorageOperationError; isLoading: boolean; isFetching: boolean; reload: () => void };

export function useShareRead<T>({ connectionId, resource, purpose, enabled, start }: Args<T>): ShareRead<T> {
  const queryClient = useQueryClient();
  const resourceKey = storageResourceKey(resource);
  const queryKey = useMemo(
    () => [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(resource), resourceKey, `share-dialog:${purpose}`] as const,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the resource identity is its key
    [connectionId, resourceKey, purpose],
  );
  const query = useQuery({
    queryKey,
    enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<T> => {
      const handle = start();
      const onAbort = () => handle.cancel();
      signal.addEventListener('abort', onAbort);
      try {
        const result = await handle.promise;
        if (!result.ok) throw result.error;
        return result.value;
      } finally {
        signal.removeEventListener('abort', onAbort);
      }
    },
  });
  useEffect(() => () => {
    if ((queryClient.getQueryCache().find({ queryKey, exact: true })?.getObserversCount() ?? 0) > 0) return;
    void queryClient.cancelQueries({ queryKey, exact: true });
    queryClient.removeQueries({ queryKey, exact: true });
  }, [queryClient, queryKey]);
  return {
    data: query.data,
    error: query.error ? toStorageOperationError(query.error) : undefined,
    isLoading: enabled && query.isPending,
    isFetching: query.isFetching,
    reload: () => void queryClient.invalidateQueries({ queryKey, exact: true }),
  };
}
